import React, { useEffect, useMemo, useState } from 'react';
import { MapContainer, Marker, TileLayer, useMap } from 'react-leaflet';
import type { LatLngExpression } from 'leaflet';
import L from 'leaflet';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation, useNavigate } from 'react-router-dom';
import { t } from 'i18next';

import { userLocation } from '../../../store/selectors/location';
import { savePickup } from '../../../store/slices/pickup';
import { getPickup } from '../../../store/selectors/pickup';
import { setFulfillmentMode } from '../../../store/slices/fulfillment';
import { selectBasketItems } from '../../../store/selectors/basket';
import { useGetPickupBranches } from '../../restaurant/services/getBranches';
import { IBranchList } from '../../restaurant/types/Branch';
import {
  applyBranchAvailability,
  checkBranchAvailability,
} from '../services/checkBranchAvailability';
import { setBasketFromServer } from '../../../store/slices/basketSlice';

import { Button } from '../../../components/ui/button';
import { Card } from '../../../components/ui/card';
import { Separator } from '../../../components/ui/separator';
import { Input } from '../../../components/ui/input';
import { SearchIcon } from '../../../assets/Search';
import { cn } from '../../../lib/utils';
import {
  Drawer,
  DrawerContent,
  DrawerTitle,
} from '../../../components/ui/drawer';
import { DeliveryTimePicker } from '../../../components/DeliveryTimePicker';
import { custonSooner } from '../../../components/CustopmSooner';
import { LocationFooterIcon } from '../../../assets/footer/Location';
import { ArrowIcon } from '../../../assets/Arrow';
import { ArrowRightIcon } from '../../../assets/ArrowRight';
import { ClockIcon } from '../../../assets/Clock';
import { PhoneIcon } from '../../../assets/Phone';
import { ListIcon } from '../../../assets/List';
import { CopyIcon } from '../../../assets/Copy';
import { XIcon } from '../../../assets/X';
import { Maximize } from 'lucide-react';
import { useTelegramBackButton } from '../../../utils/BackButton';
import { requestTelegramLocation } from '../../../lib/TelegramLocation';
import { getDistanceInMeters } from '../../../utils/distance';
import { formatPhoneNumber, toTelHref } from '../../../utils/formatPhone';
import {
  PICKUP_MIN_HOURS_FROM_NOW,
  getPickupMaxTime,
} from '../../../utils/pickupWindow';
import {
  impactOccuer,
  notificationOccurred,
} from '../../../utils/hapticEvents';

// Hoisted: declared inside the component this was a new component type on
// every render, so React remounted it and re-ran setView — snapping the map
// back to `mapCenter` on any unrelated state change, e.g. each keystroke in
// the branch search.
const MapUpdater = ({
  center,
  zoom,
}: {
  center: LatLngExpression;
  zoom: number;
}) => {
  const map = useMap();

  useEffect(() => {
    map.setView(center, zoom);
  }, [center, zoom, map]);

  return null;
};

const CAPITAL_CENTER: [number, number] = [41.2995, 69.2401];
const DEFAULT_ZOOM = 16;
const SELECTED_BRANCH_ZOOM = 17;
// Fits all of Tashkent, used when opening the pickup map with no branch
// chosen yet — so the user can see every option, not just what's nearby.
const CITY_ZOOM = 11;
const CITY_CENTER: [number, number] = [41.26822774159865, 69.26691229222344];

export const PickupMap: React.FC = () => {
  const navigate = useNavigate();
  const routerLocation = useLocation();
  const dispatch = useDispatch();

  const routerState = routerLocation.state as
    | { returnTo?: string }
    | undefined;

  // Without a back button Telegram shows "Close", which quits the Mini App
  // instead of returning to whatever opened the map.
  useTelegramBackButton(routerState?.returnTo ?? '/');

  const { latitude: lat, longitude: lng, hasFix } = useSelector(userLocation);
  // Branch picking only. Delivery-address picking used to share this screen
  // off a `mode` router flag, but it never got the draggable pin, so it lived
  // on /adding-address in practice — that is now the only address screen.
  const { branches } = useGetPickupBranches(
    lat ?? CAPITAL_CENTER[0],
    lng ?? CAPITAL_CENTER[1],
    true
  );
  const basketItems = useSelector(selectBasketItems);

  const locationUser: [number, number] = [
    lat ?? CAPITAL_CENTER[0],
    lng ?? CAPITAL_CENTER[1],
  ];

  // Reopening the map with a branch already chosen should land on that branch,
  // not send the user hunting for it from their own location again.
  const savedPickup = useSelector(getPickup);
  const savedPickupBranchId = Number(savedPickup?.branch?._id) || 0;
  const savedPickupCenter: [number, number] | null =
    savedPickupBranchId && savedPickup?.branch?.location?.lat
      ? [savedPickup.branch.location.lat, savedPickup.branch.location.lon]
      : null;

  const [mapCenter, setMapCenter] = useState<LatLngExpression>(
    () => savedPickupCenter ?? CITY_CENTER
  );
  const [mapZoom, setMapZoom] = useState(() =>
    savedPickupCenter ? SELECTED_BRANCH_ZOOM : CITY_ZOOM
  );
  const [activeBranch, setActiveBranch] = useState<IBranchList>();

  const [showTimePicker, setShowTimePicker] = useState(false);
  const [pickupOn, setPickupOn] = useState<string | null>(null);
  const pickupMaxTime = getPickupMaxTime(new Date());
  const [isListOpen, setIsListOpen] = useState(false);
  const [branchQuery, setBranchQuery] = useState('');

  const [userLat, userLng] = locationUser;

  const filteredBranches = useMemo(() => {
    const query = branchQuery.trim().toLowerCase();
    const matched = query
      ? branches.filter(branch =>
          `${branch.title} ${branch.address}`.toLowerCase().includes(query)
        )
      : branches;

    // Chosen branch pinned to the top, everything else nearest-first.
    return [...matched].sort((a, b) => {
      if (a.id === savedPickupBranchId) return -1;
      if (b.id === savedPickupBranchId) return 1;

      return (
        getDistanceInMeters(userLat, userLng, Number(a.lat), Number(a.long)) -
        getDistanceInMeters(userLat, userLng, Number(b.lat), Number(b.long))
      );
    });
  }, [branches, branchQuery, savedPickupBranchId, userLat, userLng]);

  const [isCheckingAvailability, setIsCheckingAvailability] = useState(false);

  const branchMarkerIcon = useMemo(
    () =>
      L.divIcon({
        className: 'pickup-branch-marker',
        html: '<span class="block h-5 w-5 rounded-full border-4 border-[#007854] bg-white"></span>',
        iconSize: [20, 20],
        iconAnchor: [10, 20],
      }),
    []
  );

  // Replaces the plain circle on the chosen branch. Anchored at the base of
  // the stem (not the middle of the head) so the marker stands on the exact
  // branch coordinates, the same point the circle occupied.
  const selectedBranchMarkerIcon = useMemo(
    () =>
      L.divIcon({
        className: 'pickup-branch-marker pickup-branch-marker-active',
        html: `
          <svg width="42" height="68" viewBox="0 0 42 68" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path fill-rule="evenodd" clip-rule="evenodd" d="M21 0C32.598 0 42 9.40202 42 21C42 31.9234 33.6599 40.8989 23 41.906V64C23 65.1046 22.1046 66 21 66C19.8954 66 19 65.1046 19 64V41.906C8.34014 40.8989 0 31.9234 0 21C0 9.40202 9.40202 0 21 0Z" fill="#2BB747"/>
            <path d="M28 21C28 17.134 24.866 14 21 14C17.134 14 14 17.134 14 21C14 24.866 17.134 28 21 28C24.866 28 28 24.866 28 21Z" fill="white"/>
            <path opacity="0.3" d="M21 68C23.7614 68 26 66.8807 26 65.5C26 64.1193 23.7614 63 21 63C18.2386 63 16 64.1193 16 65.5C16 66.8807 18.2386 68 21 68Z" fill="#2BB747"/>
          </svg>`,
        iconSize: [42, 68],
        iconAnchor: [21, 66],
      }),
    []
  );

  const userMarkerIcon = useMemo(
    () =>
      L.icon({
        iconUrl: '/assets/pin.png',
        iconSize: [40, 40],
        iconAnchor: [20, 40],
      }),
    []
  );

  const selectBranch = (branch: IBranchList) => {
    setMapCenter([Number(branch.lat), Number(branch.long)]);
    setMapZoom(SELECTED_BRANCH_ZOOM);
    setActiveBranch(branch);
    setIsListOpen(false);
    setBranchQuery('');
  };

  const handleFindNearest = () => {
    if (!branches.length) return;
    impactOccuer('soft');

    const nearest = branches.reduce((closest, branch) => {
      const distance = getDistanceInMeters(
        locationUser[0],
        locationUser[1],
        Number(branch.lat),
        Number(branch.long)
      );
      const closestDistance = getDistanceInMeters(
        locationUser[0],
        locationUser[1],
        Number(closest.lat),
        Number(closest.long)
      );

      return distance < closestDistance ? branch : closest;
    }, branches[0]);

    selectBranch(nearest);
  };

  const confirmPickup = (branch: IBranchList, destination?: string) => {
    dispatch(setFulfillmentMode('pickup'));
    dispatch(
      savePickup({
        _id: String(branch.id),
        branch: {
          _id: String(branch.id),
          title: branch.title,
          address: branch.address,
          delivery_available: false,
          location: {
            lat: Number(branch.lat),
            lon: Number(branch.long),
          },
        },
      })
    );
    notificationOccurred('success');
    // Go back to whichever screen opened the map, not always the basket.
    navigate(destination ?? routerState?.returnTo ?? '/');
  };

  const handleConfirmBranch = async () => {
    if (!activeBranch) return;
    impactOccuer('soft');

    setIsCheckingAvailability(true);

    try {
      const { branchMenu } = await checkBranchAvailability(
        activeBranch.id,
        basketItems
      );

      // Nothing is removed and nothing is announced here — the basket is just
      // marked against this branch, and anything missing surfaces in the cart
      // with its own "Изменить" action.
      if (branchMenu.length && basketItems.length) {
        dispatch(
          setBasketFromServer(
            applyBranchAvailability(basketItems, branchMenu)
          )
        );
      }

      confirmPickup(activeBranch);
    } catch {
      // Couldn't verify stock; don't block the user or touch their basket.
      confirmPickup(activeBranch);
    } finally {
      setIsCheckingAvailability(false);
    }
  };

  const handleCopyAddress = () => {
    if (!activeBranch?.address) return;
    navigator.clipboard?.writeText(activeBranch.address);
    impactOccuer('light');
    custonSooner.success('Адрес скопирован');
  };

  const renderBranchCard = (branch: IBranchList) => (
    <Card
      key={branch.id}
      data-testid="pickup-map-branch-card"
      data-branch-id={branch.id}
      className={cn(
        'flex flex-col p-3 rounded-2xl space-y-3 bg-fill-base border border-transparent transition-opacity active:opacity-70',
        branch.id === savedPickupBranchId && 'border-brand-green'
      )}
      onClick={() => selectBranch(branch)}
    >
      <div className="flex flex-row items-start justify-between gap-x-2">
        <div className="flex flex-col min-w-0">
          <p className="text-body-base-medium">{branch.title}</p>
          {branch.address && (
            <p className="text-body-sm-regular text-text-tertiary">
              {branch.address}
            </p>
          )}
        </div>
        <div className="w-6 h-6 shrink-0 rounded-full bg-fill-background flex items-center justify-center">
          <ArrowRightIcon className="text-text-icon-secondary" />
        </div>
      </div>
      <Separator />
      <div className="flex flex-row items-center justify-between gap-x-2">
        <div className="flex flex-row items-center space-x-2 min-w-0">
          <PhoneIcon className="text-text-icon-secondary shrink-0" />
          <a
            href={toTelHref(branch.phone)}
            onClick={e => e.stopPropagation()}
            className="text-caption-sm-medium text-text-tertiary truncate"
          >
            {formatPhoneNumber(branch.phone)}
          </a>
        </div>
        <div className="flex flex-row items-center space-x-2 min-w-0">
          <ClockIcon className="text-text-icon-secondary shrink-0" />
          <p className="text-caption-sm-medium text-text-tertiary truncate">
            {branch.working_hours}
          </p>
        </div>
      </div>
    </Card>
  );

  return (
    // Fixed, not absolute: vaul sets `position: fixed` on <body> while a
    // drawer is open, which would otherwise make this resolve against a
    // zero-height body and hide the map behind the sheet.
    <div
      data-testid="pickup-map-screen"
      style={{ position: 'fixed', top: 0, bottom: 0, left: 0, right: 0 }}
    >
      <MapContainer
        center={mapCenter}
        zoom={mapZoom}
        style={{ height: '100%', width: '100%', zIndex: 1 }}
      >
        <TileLayer
          attribution="&copy; OpenStreetMap contributors"
          url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
        />
        <MapUpdater center={mapCenter} zoom={mapZoom} />
        {branches.map(branch => {
          // Until something is tapped, the previously saved branch is the one
          // shown as chosen — the sheet stays closed, only the pin is set.
          const isActive =
            branch.id === (activeBranch?.id ?? savedPickupBranchId);

          return (
            <Marker
              key={branch.id}
              position={[Number(branch.lat), Number(branch.long)]}
              icon={isActive ? selectedBranchMarkerIcon : branchMarkerIcon}
              zIndexOffset={isActive ? 1000 : 0}
              eventHandlers={{
                click: () => {
                  impactOccuer('soft');
                  selectBranch(branch);
                },
              }}
            />
          );
        })}
        {/* Only with a real fix — the store seeds the Tashkent centre, and
            pinning "you are here" on it is worse than showing nothing. */}
        {hasFix && <Marker position={locationUser} icon={userMarkerIcon} />}
      </MapContainer>

      <div className="absolute top-safe-top right-0 z-20 px-4 pt-4">
        <Button
          data-testid="pickup-map-list-button"
          className="h-10 bg-white rounded-full px-4 flex flex-row items-center gap-x-[6px] shadow-[0px_2px_16px_0px_#00000014]"
          onClick={() => {
            impactOccuer('soft');
            setIsListOpen(true);
          }}
        >
          <ListIcon className="text-text-icon-secondary" />
          <p className="text-display-sm text-text-tertiary">{t('list')}</p>
        </Button>
      </div>

      {!activeBranch && (
        <div className="absolute bottom-0 z-20 left-0 right-0 flex flex-col items-center px-4 pb-safe-bottom">
          <div className="w-full flex flex-col items-end gap-2 pb-4">
            <Button
              className="h-12 bg-white min-w-12 rounded-full active:scale-95 transition-transform duration-150 text-text-icon-tertiary"
              onClick={() => {
                impactOccuer('soft');
                setMapCenter(CITY_CENTER);
                setMapZoom(CITY_ZOOM);
              }}
            >
              <Maximize className="w-5 h-5" />
            </Button>
            <Button
              className="h-12 bg-white min-w-12 rounded-full active:scale-95 transition-transform duration-150 text-text-icon-tertiary"
              onClick={() => {
                requestTelegramLocation();
                setMapCenter(locationUser);
                setMapZoom(DEFAULT_ZOOM);
                notificationOccurred('success');
              }}
            >
              <ArrowIcon />
            </Button>
          </div>
          <Button
            onClick={handleFindNearest}
            className="w-full h-12 px-5 gap-2 bg-button-primary rounded-[32px] flex flex-row items-center justify-center mb-4"
          >
            <LocationFooterIcon className="h-6 w-6" />
            <p className="text-display-sm">{t('find-near')}</p>
          </Button>
        </div>
      )}

      <Drawer
        shouldScaleBackground={false}
        open={showTimePicker}
        onOpenChange={setShowTimePicker}
      >
        <DrawerContent
          overlayClassName="bg-transparent"
          className="border-none px-6 pb-safe-bottom max-h-[70vh]"
        >
          <div className="flex flex-col gap-3 pb-4 pt-2">
            <DrawerTitle className="sr-only">Время получения</DrawerTitle>
            <DeliveryTimePicker
              value={pickupOn || undefined}
              minHoursFromNow={PICKUP_MIN_HOURS_FROM_NOW}
              maxDateTime={pickupMaxTime}
              mode="pickup"
              onSelect={dateTime => {
                setPickupOn(dateTime);
                setShowTimePicker(false);
              }}
              onCancel={() => {
                setPickupOn(null);
                setShowTimePicker(false);
              }}
            />
          </div>
        </DrawerContent>
      </Drawer>

      <Drawer
        shouldScaleBackground={false}
        open={!!activeBranch}
        onOpenChange={open => {
          if (!open) setActiveBranch(undefined);
        }}
      >
        <DrawerContent
          data-testid="pickup-map-branch-sheet"
          overlayClassName="bg-transparent"
          className="bg-fill-background px-4 pb-safe-bottom"
        >
          <div className="flex flex-col space-y-2">
            <div className="flex flex-row justify-between items-start">
              <DrawerTitle>
                <p data-testid="pickup-map-branch-title" className="text-display-md">
                  {activeBranch?.title}
                </p>
              </DrawerTitle>
              <div
                data-testid="pickup-map-branch-close"
                className="flex items-center justify-center h-6 w-6 rounded-full bg-button-secondary shrink-0"
                onClick={() => setActiveBranch(undefined)}
              >
                <XIcon className="text-text-icon-secondary" />
              </div>
            </div>
            <div
              className="flex flex-row items-center space-x-1"
              onClick={handleCopyAddress}
            >
              <p className="text-body-sm-regular text-text-secondary">
                {activeBranch?.address}
              </p>
              <CopyIcon className="text-text-icon-secondary shrink-0" />
            </div>

            <Card className="flex flex-col pl-[18px] py-[9px] rounded-xl">
              <div className="flex flex-col">
                <div className="flex flex-row items-center space-x-[13px]">
                  <PhoneIcon className="text-text-icon-secondary" />
                  <div className="flex flex-col">
                    <p className="text-body-base-regular">{t('phone')}</p>
                    <a
                      href={toTelHref(activeBranch?.phone ?? '')}
                      className="text-body-sm-regular text-text-tertiary"
                    >
                      {formatPhoneNumber(activeBranch?.phone ?? '')}
                    </a>
                  </div>
                </div>
                <div className="pl-7 py-[9px]">
                  <Separator />
                </div>
              </div>
              <div className="flex flex-col">
                <div className="flex flex-row items-center space-x-[13px]">
                  <ClockIcon className="text-text-icon-secondary" />
                  <div className="flex flex-col">
                    <p className="text-body-base-regular">{t('work-time')}</p>
                    <p className="text-body-sm-regular text-text-tertiary">
                      {activeBranch?.working_hours}
                    </p>
                  </div>
                </div>
              </div>
            </Card>

            <div className="flex flex-col space-y-2 pb-6 pt-2 w-full">
              <Button
                data-testid="pickup-map-confirm"
                className="w-full h-12 bg-button-primary rounded-xl flex flex-row items-center justify-center space-x-1"
                onClick={handleConfirmBranch}
                disabled={isCheckingAvailability}
              >
                <p className="text-display-sm">
                  {isCheckingAvailability ? '...' : t('order-here')}
                </p>
              </Button>
              <Button
                onClick={() =>
                  window.Telegram.WebApp.openLink(
                    `https://yandex.uz/maps/10335/tashkent/?mode=routes&rtext=${locationUser.join(',')}~${activeBranch?.lat},${activeBranch?.long}&rtt=auto&ruri=~&z=12`
                  )
                }
                className="w-full h-12 bg-button-secondary rounded-xl flex flex-row items-center justify-center space-x-1"
              >
                <LocationFooterIcon className="text-text-icon-primary h-6 w-6" />
                <p className="text-display-sm text-text-primary">
                  {t('how-to-get')}
                </p>
              </Button>
            </div>
          </div>
        </DrawerContent>
      </Drawer>

      <Drawer
        shouldScaleBackground={false}
        open={isListOpen}
        onOpenChange={open => {
          setIsListOpen(open);
          // Don't greet the user with the previous search on reopen.
          if (!open) setBranchQuery('');
        }}
      >
        <DrawerContent
          data-testid="pickup-map-list"
          overlayClassName="bg-black/20"
          className="bg-fill-background px-4 pb-safe-bottom h-[70vh]"
        >
          {/* Sticky, not a sibling of the scroll area: DrawerContent puts its
              children inside one scrolling box, so the title and search would
              scroll away with the list otherwise. */}
          <div className="sticky top-0 z-10 bg-fill-background pt-3 pb-3">
            <div className="flex flex-row justify-between items-center">
              <DrawerTitle>
                <p className="text-display-md">{t('list')}</p>
              </DrawerTitle>
              <div
                className="flex items-center justify-center h-6 w-6 rounded-full bg-button-secondary"
                onClick={() => setIsListOpen(false)}
              >
                <XIcon className="text-text-icon-secondary" />
              </div>
            </div>
            <div className="pt-3">
              <Input
                data-testid="pickup-map-search"
                className="w-full rounded-xl h-12 border-none shadow-none bg-fill-base"
                placeholder={t('search-branch')}
                value={branchQuery}
                onChange={event => setBranchQuery(event.target.value)}
                iconLeft={
                  <SearchIcon className="text-text-icon-secondary w-5 h-5" />
                }
              />
            </div>
          </div>
          <div className="flex flex-col space-y-2 pb-4">
            {filteredBranches.length === 0 && (
              <p className="py-6 text-center text-body-sm-regular text-text-tertiary">
                {t('no-results')}
              </p>
            )}
            {filteredBranches.map(branch => renderBranchCard(branch))}
          </div>
        </DrawerContent>
      </Drawer>
    </div>
  );
};

export default PickupMap;
